// src/pages/AdminPanel.jsx
import React, { useState, useEffect } from 'react';
import {
  Package, TrendingUp, Menu, Tag, Percent, Settings, Activity,
  Users, FileText, Star, ShoppingCart, RotateCcw
} from 'lucide-react';

import {
  addProduct,
  getAllProducts,
  updateProduct,
  deleteProduct
} from '../services/productService';

import {
  addCampaign,
  getAllCampaigns,
  updateCampaign,
  deleteCampaign
} from '../services/discountService';

import {
  getAllOrders,
  updateOrderStatus
} from '../services/orderService';

import {
  getAllCustomers
} from '../services/customerService';

import {
  approveReturn,
  rejectReturn,
  markReturnReceived,
  completeReturn,
  getPendingReturns
} from '../services/returnService';

import {
  getAllReviewsForAdmin,
  approveReview,
  rejectReview,
  replyToReview,
  deleteReviewAdmin
} from '../services/reviewService';

import {
  getAnalytics,
  getFinancialReport
} from '../services/analyticsService';

import {
  getTopViewedProducts,
  getProductCategoryViews
} from '../services/productAnalyticsService';

// ✅ SADECE getUserTypeStats import et
import {
  getUserTypeStats
} from '../services/visitorService';

import {
  CATEGORY_CONFIG
} from '../config/categoryConfig';

import toast from 'react-hot-toast';
import AdminPanelStyles from './AdminPanelStyles';
import {
  DashboardTab,
  ProductsTab,
  DiscountsTab,
  CampaignsTab,
  ReportsTab,
  OrdersTab,
  CustomersTab,
  ReturnsTab,
  ReviewsTab,
  VisitorsTab
} from './AdminPanelTabs';
import {
  ProductModal,
  CampaignModal,
  OrderDetailsModal,
  CustomerDetailsModal,
  ReturnApprovalModal,
  ReturnReceiveModal
} from './AdminPanelModals';

function AdminPanel() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [products, setProducts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [financialData, setFinancialData] = useState(null);
  const [pendingReturnsData, setPendingReturnsData] = useState(null);
  const [userTypeData, setUserTypeData] = useState(null); // ✅ YENİ STATE

  // Modal states
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showAddCampaign, setShowAddCampaign] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [showReturnApprovalModal, setShowReturnApprovalModal] = useState(false);
  const [showReturnReceiveModal, setShowReturnReceiveModal] = useState(false);
  const [selectedReturnOrder, setSelectedReturnOrder] = useState(null);

  // UI states
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterStock, setFilterStock] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [sidebarOpen]);

  const fetchAllData = async () => {
    try {
      setRefreshing(true);

      let productsData = [];
      let campaignsData = [];
      let analyticsData = null;
      let ordersData = [];
      let customersData = [];
      let financeData = null;
      let returnsData = null;
      let reviewsData = [];
      let visitorData = null; // ✅ YENİ

      // Products
      try {
        const productsRes = await getAllProducts();
        if (productsRes.success) {
          productsData = productsRes.products;
          console.log('✅ Ürünler yüklendi:', productsData.length);
        } else {
          console.error('❌ Ürün hatası:', productsRes.error);
        }
      } catch (err) {
        console.error('❌ Ürün yükleme hatası:', err);
      }

      // Campaigns
      try {
        const campaignsRes = await getAllCampaigns();
        if (campaignsRes.success) {
          campaignsData = campaignsRes.campaigns;
          console.log('✅ Kampanyalar yüklendi:', campaignsData.length);
        }
      } catch (err) {
        console.error('❌ Kampanya yükleme hatası:', err);
      }

      // Analytics
      try {
        const analyticsRes = await getAnalytics();
        if (analyticsRes.success) {
          analyticsData = analyticsRes.analytics;
          console.log('✅ Analizler yüklendi');
        }
      } catch (err) {
        console.error('❌ Analiz yükleme hatası:', err);
      }

      // Orders
      try {
        const ordersRes = await getAllOrders();
        console.log('📦 getAllOrders sonucu:', ordersRes);

        if (ordersRes.success && ordersRes.orders) {
          ordersData = ordersRes.orders;
          console.log('✅ Siparişler yüklendi:', ordersData.length);

          if (ordersData.length > 0) {
            console.log('📦 İlk sipariş:', {
              id: ordersData[0].id,
              orderNumber: ordersData[0].orderNumber,
              status: ordersData[0].status,
              total: ordersData[0].total,
              createdAt: ordersData[0].createdAt,
              customerName: ordersData[0].customerName
            });
          }
        } else {
          console.error('❌ Sipariş hatası:', ordersRes.error);
          toast.error('⚠️ Siparişler yüklenemedi: ' + ordersRes.error);
        }
      } catch (err) {
        console.error('❌ Sipariş yükleme hatası:', err);
        toast.error('❌ Siparişler yüklenirken hata: ' + err.message);
      }

      // Customers
      try {
        const customersRes = await getAllCustomers();
        if (customersRes.success) {
          customersData = customersRes.customers;
          console.log('✅ Müşteriler yüklendi:', customersData.length);

          if (customersData.length > 0) {
            console.log('👤 İlk müşteri:', {
              id: customersData[0].id,
              email: customersData[0].email,
              name: customersData[0].name,
              totalOrders: customersData[0].totalOrders,
              totalSpent: customersData[0].totalSpent
            });
          }
        }
      } catch (err) {
        console.error('❌ Müşteri yükleme hatası:', err);
      }

      // Finance
      try {
        const financeRes = await getFinancialReport();
        if (financeRes.success) {
          financeData = financeRes.data;
          console.log('✅ Finans raporu yüklendi');
        }
      } catch (err) {
        console.error('❌ Finans yükleme hatası:', err);
      }

      // Returns
      try {
        const returnsRes = await getPendingReturns();
        if (returnsRes.success) {
          returnsData = returnsRes;
          console.log('✅ İadeler yüklendi');
        }
      } catch (err) {
        console.error('❌ İade yükleme hatası:', err);
      }

      // Reviews
      try {
        const reviewsRes = await getAllReviewsForAdmin();
        if (reviewsRes.success) {
          reviewsData = reviewsRes.reviews;
          console.log('✅ Değerlendirmeler yüklendi:', reviewsData.length);
        } else {
          console.error('❌ Değerlendirme hatası:', reviewsRes.error);
        }
      } catch (err) {
        console.error('❌ Değerlendirme yükleme hatası:', err);
      }

      // ✅ Visitor User Type Stats (SADECE KULLANICI TİPİ)
      try {
        const visitorRes = await getUserTypeStats(30);
        if (visitorRes.success) {
          visitorData = visitorRes;
          console.log('✅ Ziyaretçi user type yüklendi:', visitorData);
        }
      } catch (err) {
        console.error('❌ Ziyaretçi yükleme hatası:', err);
      }

      setProducts(productsData);
      setCampaigns(campaignsData);
      setAnalytics(analyticsData);
      setOrders(ordersData);
      setCustomers(customersData);
      setFinancialData(financeData);
      setPendingReturnsData(returnsData);
      setReviews(reviewsData);
      setUserTypeData(visitorData); // ✅ YENİ STATE

      console.log('🎯 Tüm veriler state\'e yazıldı:', {
        products: productsData.length,
        campaigns: campaignsData.length,
        orders: ordersData.length,
        customers: customersData.length,
        reviews: reviewsData.length,
        visitors: visitorData
      });

    } catch (error) {
      console.error('❌ Genel veri yükleme hatası:', error);
      toast.error('Veriler yüklenirken hata oluştu: ' + error.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const stats = analytics || {
    totalProducts: products.length,
    activeProducts: products.filter(p => p.isActive !== false).length,
    totalRevenue: products.reduce((sum, p) => sum + ((p.currentPrice || p.price) * p.stock), 0),
    totalStock: products.reduce((sum, p) => sum + p.stock, 0),
    lowStockProducts: products.filter(p => p.stock < (p.minStock || 5)).length,
    discountedProducts: products.filter(p => p.discount > 0).length,
    activeCampaigns: campaigns.filter(c => new Date(c.endDate) > new Date()).length,
    totalOrders: orders.length,
    pendingOrders: orders.filter(o => o.status === 'pending').length,
    completedOrders: orders.filter(o => o.status === 'completed').length,
    totalCustomers: customers.length,
    pendingReturns: orders.filter(o => o.returnStatus === 'pending').length,
    approvedReturns: orders.filter(o => o.returnStatus === 'approved').length,
    todayRevenue: orders
      .filter(o => new Date(o.createdAt).toDateString() === new Date().toDateString())
      .reduce((sum, o) => sum + o.total, 0),
    monthlyRevenue: orders
      .filter(o => {
        const orderDate = new Date(o.createdAt);
        const now = new Date();
        return orderDate.getMonth() === now.getMonth() && orderDate.getFullYear() === now.getFullYear();
      })
      .reduce((sum, o) => sum + o.total, 0),
    totalReviews: reviews.length,
    pendingReviews: reviews.filter(r => r.status === 'pending').length,
    activeReviews: reviews.filter(r => r.status === 'active').length,
    rejectedReviews: reviews.filter(r => r.status === 'rejected').length,
    averageRating: reviews.length > 0
      ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
      : 0
  };

  // Filtreleme ve Sıralama
  const filteredProducts = products
    .filter(p => {
      const matchesSearch = p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.sku?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory = filterCategory === 'all' || p.category === filterCategory;
      const matchesStatus = filterStatus === 'all' ||
        (filterStatus === 'active' && p.isActive !== false) ||
        (filterStatus === 'inactive' && p.isActive === false);

      const matchesStock = filterStock === 'all' ||
        (filterStock === 'instock' && p.stock > (p.minStock || 5)) ||
        (filterStock === 'lowstock' && p.stock > 0 && p.stock <= (p.minStock || 5)) ||
        (filterStock === 'outofstock' && p.stock === 0);

      return matchesSearch && matchesCategory && matchesStatus && matchesStock;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'price-asc': return (a.currentPrice || a.price) - (b.currentPrice || b.price);
        case 'price-desc': return (b.currentPrice || b.price) - (a.currentPrice || a.price);
        case 'stock-asc': return a.stock - b.stock;
        case 'stock-desc': return b.stock - a.stock;
        case 'name': return a.name.localeCompare(b.name);
        case 'discount': return (b.discount || 0) - (a.discount || 0);
        case 'sales': return (b.sales || 0) - (a.sales || 0);
        default: return 0;
      }
    });

  // Handlers
  const handleAddProduct = async (productData, imageFiles, videoFiles) => {
    console.log('📸 Resimler:', imageFiles.length);
    console.log('🎥 Videolar:', videoFiles.length);

    const result = await addProduct(productData, imageFiles, videoFiles);
    if (result.success) {
      toast.success('✅ Ürün başarıyla eklendi');
      setShowAddProduct(false);
      fetchAllData();
    } else {
      toast.error('❌ Hata: ' + result.error);
    }
  };

  const handleUpdateProduct = async (productId, productData, imageFiles, videoFiles) => {
    try {
      setRefreshing(true);
      console.log('📸 Yeni resimler:', imageFiles.length);
      console.log('🎥 Yeni videolar:', videoFiles.length);

      const result = await updateProduct(productId, productData, imageFiles, videoFiles);

      if (result.success) {
        toast.success('✅ Ürün başarıyla güncellendi');
        setEditingProduct(null);

        if (result.product) {
          setProducts(prevProducts =>
            prevProducts.map(p => p.id === productId ? result.product : p)
          );
        }

        await fetchAllData();
      } else {
        toast.error('❌ Hata: ' + result.error);
      }
    } catch (error) {
      console.error('Güncelleme hatası:', error);
      toast.error('❌ Bir hata oluştu');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeleteProduct = async (product) => {
    if (window.confirm(`"${product.name}" ürününü silmek istediğinizden emin misiniz?`)) {
      const imageUrls = product.images || (product.image ? [product.image] : []);
      const videoUrls = product.videos || [];

      console.log('🗑️ Silinecek resimler:', imageUrls.length);
      console.log('🗑️ Silinecek videolar:', videoUrls.length);

      const result = await deleteProduct(product.id, imageUrls, videoUrls);
      if (result.success) {
        toast.success('✅ Ürün başarıyla silindi');
        fetchAllData();
      } else {
        toast.error('❌ Hata: ' + result.error);
      }
    }
  };

  const handleAddCampaign = async (campaignData) => {
  const result = await addCampaign(campaignData);
  if (result.success) {
    toast.success('✅ Kampanya başarıyla oluşturuldu ve ürünlere uygulandı');
    setShowAddCampaign(false);
    fetchAllData();
  } else {
    toast.error('❌ Hata: ' + result.error);
  }
};

  const handleUpdateCampaign = async (campaignId, campaignData) => {
  const result = await updateCampaign(campaignId, campaignData);
  if (result.success) {
    toast.success('✅ Kampanya başarıyla güncellendi ve ürünlere yeniden uygulandı');
    setEditingCampaign(null);
    fetchAllData();
  } else {
    toast.error('❌ Hata: ' + result.error);
  }
};

  const handleDeleteCampaign = async (campaign) => {
  if (window.confirm(`"${campaign.title}" kampanyasını silmek istediğinizden emin misiniz?\n\nÜrünlerden indirim kaldırılacak.`)) {
    const result = await deleteCampaign(campaign.id);
    if (result.success) {
      toast.success('✅ Kampanya silindi ve indirim kaldırıldı');
      fetchAllData();
    } else {
      toast.error('❌ Hata: ' + result.error);
    }
  }
};

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    const result = await updateOrderStatus(orderId, newStatus);
    if (result.success) {
      toast.success('✅ Sipariş durumu güncellendi');
      fetchAllData();
    } else {
      toast.error('❌ Hata: ' + result.error);
    }
  };

  // İade İşlemleri
  const handleApproveReturn = async (orderId, approvalData) => {
    const result = await approveReturn(orderId, {
      adminId: 'admin',
      note: approvalData.note || '',
      returnAddress: approvalData.returnAddress || 'İade Merkezi, Antalya',
      instructions: approvalData.instructions || 'Lütfen ürünü orijinal ambalajında kargoya veriniz.'
    });

    if (result.success) {
      toast.success('✅ ' + result.message);
      fetchAllData();
    } else {
      toast.error('❌ ' + result.error);
    }
  };

  const handleRejectReturn = async (orderId, reason) => {
    if (!reason || reason.trim() === '') {
      toast.error('Lütfen red nedeni giriniz');
      return;
    }

    const result = await rejectReturn(orderId, {
      adminId: 'admin',
      reason: reason
    });

    if (result.success) {
      toast.success('✅ ' + result.message);
      fetchAllData();
    } else {
      toast.error('❌ ' + result.error);
    }
  };

  const handleMarkReturnReceived = async (orderId, productCondition = 'good', inspectionNote = '') => {
    const result = await markReturnReceived(orderId, {
      adminId: 'admin',
      productCondition: productCondition,
      inspectionNote: inspectionNote
    });

    if (result.success) {
      toast.success('✅ ' + result.message);
      fetchAllData();
    } else {
      toast.error('❌ ' + result.error);
    }
  };

  const handleCompleteReturn = async (orderId) => {
    const result = await completeReturn(orderId, {
      adminId: 'admin',
      refundTransactionId: `REF-${Date.now()}`
    });

    if (result.success) {
      toast.success('✅ ' + result.message);
      fetchAllData();
    } else {
      toast.error('❌ ' + result.error);
    }
  };

  // Değerlendirme Handler'ları
  const handleApproveReview = async (reviewId) => {
    try {
      const result = await approveReview(reviewId);
      if (result.success) {
        toast.success('✅ ' + result.message);
        await fetchAllData();
      } else {
        toast.error('❌ ' + result.error);
      }
    } catch (error) {
      console.error('❌ Onaylama hatası:', error);
      toast.error('❌ Bir hata oluştu');
    }
  };

  const handleRejectReview = async (reviewId, reason) => {
    try {
      const result = await rejectReview(reviewId, reason);
      if (result.success) {
        toast.success('✅ ' + result.message);
        await fetchAllData();
      } else {
        toast.error('❌ ' + result.error);
      }
    } catch (error) {
      console.error('❌ Reddetme hatası:', error);
      toast.error('❌ Bir hata oluştu');
    }
  };

  const handleDeleteReviewAdmin = async (reviewId) => {
    try {
      const result = await deleteReviewAdmin(reviewId);
      if (result.success) {
        toast.success('✅ ' + result.message);
        await fetchAllData();
      } else {
        toast.error('❌ ' + result.error);
      }
    } catch (error) {
      console.error('❌ Silme hatası:', error);
      toast.error('❌ Bir hata oluştu');
    }
  };

  const handleReplyToReview = async (reviewId, message) => {
    try {
      const result = await replyToReview(reviewId, message);
      if (result.success) {
        toast.success('✅ ' + result.message);
        await fetchAllData();
      } else {
        toast.error('❌ ' + result.error);
      }
    } catch (error) {
      console.error('❌ Yanıt hatası:', error);
      toast.error('❌ Bir hata oluştu');
    }
  };

  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const selectAll = () => {
    setSelectedProducts(filteredProducts.map(p => p.id));
  };

  const deselectAll = () => {
    setSelectedProducts([]);
  };

  const exportProducts = () => {
    const csv = [
      ['SKU', 'Ürün Adı', 'Kategori', 'Orijinal Fiyat', 'İndirimli Fiyat', 'Stok', 'İndirim %', 'Durum'],
      ...products.map(p => [
        p.sku || '',
        p.name,
        p.category || '',
        p.originalPrice || p.price,
        p.currentPrice || p.price,
        p.stock,
        p.discount || 0,
        p.isActive !== false ? 'Aktif' : 'Pasif'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `urunler-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    setSidebarOpen(false);
  };

  return (
    <div className="admin-panel">
      <AdminPanelStyles />

      {/* Header */}
      <header className="admin-header">
        <div className="header-left">
          <button className="menu-btn" onClick={() => setSidebarOpen(true)}>
            <Menu size={24} />
          </button>
          <h1>🛒 E-TİCARET YÖNETİM PANELİ</h1>
        </div>
        <div className="header-right">
          <span className="user-name">Admin</span>
          <div className="avatar">A</div>
        </div>
      </header>

      <div className="admin-layout">
        {/* Sidebar Overlay */}
        <div
          className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
          onClick={() => setSidebarOpen(false)}
        />

        {/* Sidebar */}
        <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="nav-section">
            <div className="nav-section-title">Ana Menü</div>
            {[
              { id: 'dashboard', icon: TrendingUp, label: 'Dashboard', badge: null },
              { id: 'orders', icon: ShoppingCart, label: 'Siparişler', badge: stats.pendingOrders },
              { id: 'products', icon: Package, label: 'Ürünler', badge: products.length },
              { id: 'customers', icon: Users, label: 'Müşteriler', badge: customers.length },
              {
                id: 'reviews',
                icon: Star,
                label: 'Değerlendirmeler',
                badge: stats.pendingReviews
              },
              { id: 'campaigns', icon: Tag, label: 'Kampanyalar', badge: stats.activeCampaigns },
              {
                id: 'returns',
                icon: RotateCcw,
                label: 'İade Yönetimi',
                badge: stats.pendingReturns
              },
              {
                id: 'visitors',
                icon: Activity,
                label: 'Ziyaretçiler',
                badge: null
              },
            ].map(item => (
              <button
                key={item.id}
                className={`nav-btn ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => handleTabClick(item.id)}
              >
                <item.icon size={20} />
                <span className="nav-label">{item.label}</span>
                {item.badge !== null && item.badge > 0 && (
                  <span className="nav-badge">{item.badge}</span>
                )}
              </button>
            ))}
          </div>

          <div className="nav-section">
            <div className="nav-section-title">Finans & Raporlar</div>
            {[
              { id: 'reports', icon: FileText, label: 'Raporlar' }
            ].map(item => (
              <button
                key={item.id}
                className={`nav-btn ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => handleTabClick(item.id)}
              >
                <item.icon size={20} />
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* Main Content */}
        <main className="main-content">
          {activeTab === 'dashboard' && (
            <DashboardTab stats={stats} products={products} campaigns={campaigns} orders={orders} />
          )}

          {activeTab === 'orders' && (
            <OrdersTab
              orders={orders}
              onOrderClick={setSelectedOrder}
              onStatusUpdate={handleUpdateOrderStatus}
              refreshing={refreshing}
              fetchAllData={fetchAllData}
            />
          )}

          {activeTab === 'products' && (
            <ProductsTab
              filteredProducts={filteredProducts}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              filterCategory={filterCategory}
              setFilterCategory={setFilterCategory}
              filterStatus={filterStatus}
              setFilterStatus={setFilterStatus}
              filterStock={filterStock}
              setFilterStock={setFilterStock}
              sortBy={sortBy}
              setSortBy={setSortBy}
              categoryConfig={CATEGORY_CONFIG}
              selectedProducts={selectedProducts}
              toggleProductSelection={toggleProductSelection}
              selectAll={selectAll}
              deselectAll={deselectAll}
              setShowAddProduct={setShowAddProduct}
              setEditingProduct={setEditingProduct}
              handleDeleteProduct={handleDeleteProduct}
              exportProducts={exportProducts}
              refreshing={refreshing}
              fetchAllData={fetchAllData}
              loading={loading}
            />
          )}

          {activeTab === 'customers' && (
            <CustomersTab
              customers={customers}
              onCustomerClick={setSelectedCustomer}
            />
          )}

          {activeTab === 'reviews' && (
            <ReviewsTab
              reviews={reviews}
              onApproveReview={handleApproveReview}
              onRejectReview={handleRejectReview}
              onDeleteReview={handleDeleteReviewAdmin}
              onReplyToReview={handleReplyToReview}
              refreshing={refreshing}
              fetchAllData={fetchAllData}
            />
          )}

          {activeTab === 'discounts' && (
            <DiscountsTab
              products={products}
            />
          )}

          {activeTab === 'campaigns' && (
            <CampaignsTab
              campaigns={campaigns}
              setShowAddCampaign={setShowAddCampaign}
              setEditingCampaign={setEditingCampaign}
              handleDeleteCampaign={handleDeleteCampaign}
            />
          )}

          {activeTab === 'returns' && (
            <ReturnsTab
              orders={orders}
              onApproveReturn={(orderId) => {
                setSelectedReturnOrder(orderId);
                setShowReturnApprovalModal(true);
              }}
              onRejectReturn={(orderId) => {
                const reason = prompt('Red nedeni:');
                if (reason) handleRejectReturn(orderId, reason);
              }}
              onMarkReturnReceived={(orderId) => {
                setSelectedReturnOrder(orderId);
                setShowReturnReceiveModal(true);
              }}
              onCompleteReturn={handleCompleteReturn}
              refreshing={refreshing}
              fetchAllData={fetchAllData}
            />
          )}

          {/* ✅ GÜNCELLENMIS: Sadece userTypeData gönder */}
          {activeTab === 'visitors' && (
            <VisitorsTab
              refreshing={refreshing}
              onRefresh={fetchAllData}
              userTypeData={userTypeData}
              getTopViewedProducts={getTopViewedProducts}
              getProductCategoryViews={getProductCategoryViews}
            />
          )}

          {activeTab === 'reports' && (
            <ReportsTab products={products} campaigns={campaigns} orders={orders} />
          )}
        </main>
      </div>

      {/* Modals */}
      {showAddProduct && (
        <ProductModal
          onClose={() => setShowAddProduct(false)}
          onSave={handleAddProduct}
          categoryConfig={CATEGORY_CONFIG}
        />
      )}

      {editingProduct && (
        <ProductModal
          product={editingProduct}
          onClose={() => setEditingProduct(null)}
          onSave={(data, imageFiles, videoFiles) =>
            handleUpdateProduct(editingProduct.id, data, imageFiles, videoFiles)
          }  // ✅ 4 parametre alıyor (id, data, images, videos)
          categoryConfig={CATEGORY_CONFIG}
        />
      )}

      {showAddCampaign && (
        <CampaignModal
          onClose={() => setShowAddCampaign(false)}
          onSave={handleAddCampaign}
          categoryConfig={CATEGORY_CONFIG}
          products={products}
        />
      )}

      {editingCampaign && (
        <CampaignModal
          campaign={editingCampaign}
          onClose={() => setEditingCampaign(null)}
          onSave={(data) => handleUpdateCampaign(editingCampaign.id, data)}
          categoryConfig={CATEGORY_CONFIG}
          products={products}
        />
      )}

      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onStatusUpdate={handleUpdateOrderStatus}
        />
      )}

      {selectedCustomer && (
        <CustomerDetailsModal
          customer={selectedCustomer}
          orders={orders.filter(o => {
            return o.customerId === selectedCustomer.id ||
              o.customerEmail === selectedCustomer.email ||
              o.email === selectedCustomer.email;
          })}
          onClose={() => setSelectedCustomer(null)}
        />
      )}

      {showReturnApprovalModal && selectedReturnOrder && (
        <ReturnApprovalModal
          orderId={selectedReturnOrder}
          onClose={() => {
            setShowReturnApprovalModal(false);
            setSelectedReturnOrder(null);
          }}
          onApprove={(approvalData) => {
            handleApproveReturn(selectedReturnOrder, approvalData);
            setShowReturnApprovalModal(false);
            setSelectedReturnOrder(null);
          }}
        />
      )}

      {showReturnReceiveModal && selectedReturnOrder && (
        <ReturnReceiveModal
          orderId={selectedReturnOrder}
          onClose={() => {
            setShowReturnReceiveModal(false);
            setSelectedReturnOrder(null);
          }}
          onMarkReceived={(receiptData) => {
            handleMarkReturnReceived(selectedReturnOrder, receiptData);
            setShowReturnReceiveModal(false);
            setSelectedReturnOrder(null);
          }}
        />
      )}
    </div>
  );
}

export default AdminPanel;